"use client";


import { cn } from "logic/utils/cn";
import type { PolymorphProps } from "logic/utils/polymorph";
import type { ElementType, ReactNode } from "react";

import { type ButtonDSSize, type ButtonDSVariant, captionClasses, linkClasses, variantClasses } from "./buttonVariants";
import { Spinner } from "./Spinner";

export type ButtonDSProps<T extends ElementType> = PolymorphProps<
  T,
  {
    variant?: ButtonDSVariant;
    size?: ButtonDSSize;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
    disabled?: boolean;
    /**
     * Optional caption rendered above the main label row. Only visible when `size="xl"`.
     * Figma `Size=xl` is a two-line layout — caption row (xs-400) over label+icon row (xl-500).
     * Passing `caption` on sm/md/lg has no effect.
     *
     * The caption row is only rendered when `caption` is non-nullish; with no caption the xl
     * button is a single label row. The label row is a fixed 20px (Figma sizes it by the 20px
     * icon slot, not the 14px text), so a caption sits 5px above the label — matching Figma's
     * 2px flex gap plus the 3px the text is inset inside that row.
     *
     * @default undefined
     * @example
     * <ButtonDS size="xl" caption="Step 1 of 4" leadingIcon={<ArrowIcon className="size-5" />}>
     *   Button Label
     * </ButtonDS>
     * @see Figma — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/USD.AI---Design-System?node-id=30-1155
     */
    caption?: ReactNode;
    /**
     * Renders a spinner in the leading-icon slot in place of any `leadingIcon` passed, and
     * folds into `disabled` so the button can't be re-triggered mid-flight.
     *
     * The label `children` stay visible — this does NOT swap them out for loading copy or a
     * "Loading..." string. Callers that need in-flight label text (e.g. "Approving...") must
     * still compute that themselves and pass it as `children`, same as `BaseTxButton` does today.
     *
     * @default false
     * @example
     * <ButtonDS isLoading>Submit</ButtonDS>
     */
    isLoading?: boolean;
    /**
     * Adds `w-full` on top of the button's `inline-flex` base so it stretches to fill its
     * container. Does NOT change `inline-flex` to `flex`, and does NOT affect the width of the
     * xl caption row or label row — those remain intrinsically sized within the full-width box.
     *
     * @default false
     * @example
     * <ButtonDS fullWidth>Continue</ButtonDS>
     */
    fullWidth?: boolean;
  }
>;

// Shared layout for the 5 filled/outlined/ghost variants at xs/sm/md/lg (xl is a different
// two-line layout — not another row here). JIT-safe: complete literal strings, no interpolation.
// Boxed sizes step height and padding but share `xl-500` from md up — sm is the only one that
// drops to `lg-500`. This is NOT the `link` ramp below, which steps its font at every size.
// `xs` (height/button/xxs = 24px) only exists in Figma for secondary/outline/ghost/destructive —
// primary renders it too since variantClasses has no per-size split, but there's no Figma cell to
// verify it against.
const sizeClasses: Record<Exclude<ButtonDSSize, "xl">, string> = {
  xs: "h-6 gap-1 px-2 py-1 text-label-md-400",
  sm: "h-8 gap-1 px-3 py-2 text-label-lg-500",
  md: "h-9 gap-1 px-4 py-2 text-label-xl-500",
  lg: "h-10 gap-1 px-5 py-3 text-label-xl-500",
};

// `link` renders as inline text — no height/padding box, per Figma (16/20px line, no bg).
// Figma has no Link xl cell (falls back to lg) or Link xs cell (falls back to sm, the nearest
// smaller link size) — callers passing either fall back below. Unlike `sizeClasses`, this ramp
// steps the font at every size (12/13/14px) — matching Figma nodes `747:45831` / `126:2308` /
// `122:2180`. Do not "align" the two tables.
const linkSizeClasses: Record<Exclude<ButtonDSSize, "xl" | "xs">, string> = {
  sm: "text-label-md-500",
  md: "text-label-lg-500",
  lg: "text-label-xl-500",
};

/**
 * Design-system button, built from the Figma `Button` component set.
 *
 * Supports five filled/outlined/ghost variants plus `link`. `size="xl"` is a two-line layout
 * (caption row + label+icon row) available on filled variants only; pass `caption` to populate
 * the upper row. `link` has no xl cell in Figma and falls back to `lg` sizing when `size="xl"`.
 *
 * States (Hover/Focus/Pressed) are CSS-only — there is no `state` prop.
 * `disabled` is the only state exposed as a prop (rendered as 50% opacity).
 *
 * @see Figma — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/USD.AI---Design-System?node-id=30-1155
 *
 * @example
 * // xl two-line primary
 * <ButtonDS
 *   size="xl"
 *   caption="Step 1 of 4"
 *   leadingIcon={<ArrowIcon className="size-5" />}
 *   trailingIcon={<ChevronIcon className="size-5" />}
 * >
 *   Button Label
 * </ButtonDS>
 */
export function ButtonDS<T extends ElementType = "button">(props: ButtonDSProps<T>) {
  const {
    as: Component = "button",
    variant = "primary",
    size = "md",
    leadingIcon,
    trailingIcon,
    disabled,
    caption,
    isLoading,
    fullWidth,
    className,
    children,
    ...rest
  } = props;

  if (variant === "link") {
    // xl has no Figma link cell — treat as lg. xs has no Figma link cell either — treat as sm,
    // the nearest smaller link size. Either way the caller never gets a missing-key error.
    const linkSize = size === "xl" ? "lg" : size === "xs" ? "sm" : size;

    return (
      <Component
        type={Component == "button" ? "button" : undefined}
        {...rest}
        aria-busy={isLoading}
        className={cn(
          "relative inline-flex items-center gap-1 outline-hidden disabled:pointer-events-none disabled:opacity-50",
          linkSizeClasses[linkSize],
          linkClasses,
          fullWidth && "w-full",
          className,
        )}
        disabled={isLoading || disabled}
      >
        {isLoading ? <Spinner className="size-4" /> : leadingIcon}
        {children}
        {trailingIcon}
      </Component>
    );
  }

  if (size === "xl") {
    // Two-line layout: caption (xs-400) stacked above an inline icon+label row (xl-500).
    // height/button/xl = 48px; padding 24px × 8px; icon/label gap = 4px. The label row is a
    // fixed 20px because Figma sizes it by the 20px icon slot, not the 14px text — that 3px
    // inset on top of the 2px flex gap is what puts 5px between caption and label.
    return (
      <Component
        type={Component == "button" ? "button" : undefined}
        {...rest}
        aria-busy={isLoading}
        className={cn(
          `
            relative inline-flex flex-col items-center justify-center rounded-button outline-hidden
            disabled:pointer-events-none disabled:opacity-50
          `,
          "h-48px gap-2px px-24px py-8px text-label-xl-500",
          variantClasses[variant],
          fullWidth && "w-full",
          className,
        )}
        disabled={isLoading || disabled}
      >
        {caption != null && <span className={cn("text-label-xs-400", captionClasses[variant])}>{caption}</span>}
        <span className="inline-flex h-20px items-center gap-4px">
          {isLoading ? <Spinner className="size-4" /> : leadingIcon}
          {children}
          {trailingIcon}
        </span>
      </Component>
    );
  }

  return (
    <Component
      type={Component == "button" ? "button" : undefined}
      {...rest}
      aria-busy={isLoading}
      className={cn(
        `
          relative inline-flex items-center justify-center rounded-button outline-hidden
          disabled:pointer-events-none disabled:opacity-50
        `,
        sizeClasses[size],
        variantClasses[variant],
        fullWidth && "w-full",
        className,
      )}
      disabled={isLoading || disabled}
    >
      {isLoading ? <Spinner className="size-4" /> : leadingIcon}
      {children}
      {trailingIcon}
    </Component>
  );
}
